import { getClientId, getRedirectUri } from '../lib/helpers';
import FormAction from './FormAction';
import Input from './Input';
import { useState } from 'react';

export default function FormMagicLink(): React.JSX.Element {
  const [emailInput, setEmailInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState(false);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setEmailInput(e.target.value);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setLoading(true);
    // grab the client id and redirect uri from the url
    const clientId = getClientId();
    const redirectUri = getRedirectUri();
    if (!clientId || !redirectUri) {
      setFormError('Unable to issue magic link');
      return;
    }
    setFormError('');
    const response = await fetch(`/api/login/magiclink/email`, {
      method: 'POST',
      headers: {
        'content-type': 'application/x-www-form-urlencoded;charset=UTF-8',
        accept: 'application/json',
      },
      body: new URLSearchParams({
        email: emailInput,
        client_id: clientId,
        redirect_uri: redirectUri,
      }),
    });
    setLoading(false);
    const payload = await response.json();
    if (response.status !== 200) {
      setFormError(payload.message);
      return;
    }
    setFormSuccess(true);
    setEmailInput('');
  };

  return (
    <form className="w-full" onSubmit={handleSubmit}>
      <div className="w-full">
        {formError && (
          <div
            className="bg-eirly-100/10 border-t-4 border-eirly-500 rounded-b text-teal-900 px-4 py-3 shadow-md"
            role="alert"
          >
            <div className="flex">
              <div className="py-1">
                <svg
                  className="fill-current h-6 w-6 text-eirly-500 mr-4"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                >
                  <path d="M2.93 17.07A10 10 0 1 1 17.07 2.93 10 10 0 0 1 2.93 17.07zm12.73-1.41A8 8 0 1 0 4.34 4.34a8 8 0 0 0 11.32 11.32zM9 11V9h2v6H9v-4zm0-6h2v2H9V5z" />
                </svg>
              </div>
              <div>
                <p className="font-bold">Error</p>
                <p>{formError}</p>
              </div>
            </div>
          </div>
        )}
        {formSuccess && (
          <div
            className="bg-eirly-alt-100/10 border-t-4 border-eirly-alt-500 rounded-b text-teal-900 px-4 py-3 shadow-md"
            role="alert"
          >
            <div className="flex">
              <div className="py-1">
                <svg
                  className="fill-current h-6 w-6 text-eirly-alt-500 mr-4"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                >
                  <path d="M2.93 17.07A10 10 0 1 1 17.07 2.93 10 10 0 0 1 2.93 17.07zm12.73-1.41A8 8 0 1 0 4.34 4.34a8 8 0 0 0 11.32 11.32zM9 11V9h2v6H9v-4zm0-6h2v2H9V5z" />
                </svg>
              </div>
              <div>
                <p className="font-bold">Success</p>
                <p className="text-sm">
                  Please check your email for a magic link to login.
                </p>
              </div>
            </div>
          </div>
        )}
        <div className="text-sm grid grid-cols-1 gap-4">
          <Input
            key="magicLinkInput"
            handleChange={handleChange}
            value={emailInput}
            labelFor="magicLinkInput"
            id="magicLinkInput"
            name="magicLinkInput"
            type="email"
            isRequired={true}
            placeholder={'Enter your email'}
            extra={<FormAction text="Send" disabled={loading} tabIndex={3} />}
            extraInline={true}
            tabIndex={4}
          />
        </div>
        <div className="relative">
          <div
            className="absolute inset-0 flex items-center"
            aria-hidden="true"
          >
            <div className="w-full border-t border-gray-200" />
          </div>
          <div className="relative flex justify-center text-sm font-medium leading-6 py-4">
            <span className="bg-white px-2 text-gray-500">OR</span>
          </div>
        </div>
      </div>
    </form>
  );
}
